import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import MenuIcon from '@mui/icons-material/Menu';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { logout, userLoaded } from "../../../../redux/actions/authActions"
import { checkUserProfiles, getUserDetails } from "../../../../redux/actions/apiActions"
import { setRoleName } from "../../../../utils/setHeaders"
import setGlobalLoader from "../../../../redux/actions/loaderActions"
import { toast } from 'react-toastify';
import { PROFILES } from "../../../../utils/constants";
import HelplineBar from "../../../GeneralComponents/HelplineBar";

// Get redirect path based on user profile
const getRedirectPath = (profile) => {
  switch (profile) {
    case PROFILES.DEALER:
      return '/cars';
    case PROFILES.ADMIN:
      return '/bookings';
    case PROFILES.ORGANIZATION:
    default:
      return '/students';
  }
};

const Topbar = ({ onSidebarOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const currentProfile = useSelector(state => state.authReducer.profile);

  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(currentProfile || '');
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [switching, setSwitching] = useState(false);

  const fetchUserProfiles = async () => {
    try {
      setLoadingRoles(true);
      const userProfiles = await checkUserProfiles();
      const profiles = userProfiles?.data?.data?.profiles || [];
      setAvailableRoles(profiles);
      setLoadingRoles(false);
    } catch (error) {
      console.log('Error fetching user profiles:', error);
      setLoadingRoles(false);
    }
  };

  const handleProfileClick = () => {
    setSelectedRole(currentProfile || '');
    fetchUserProfiles();
    setRoleDialogOpen(true);
  };

  const handleRoleSwitch = async () => {
    if (selectedRole === currentProfile) {
      setRoleDialogOpen(false);
      return;
    }

    try {
      setSwitching(true);
      dispatch(setGlobalLoader(true));
      setRoleName(selectedRole);
      const userDetails = await getUserDetails();
      dispatch(userLoaded(userDetails.data.data));
      toast.success(`Switched to ${selectedRole} role successfully`);
      setRoleDialogOpen(false);
      setSwitching(false);
      const redirectPath = getRedirectPath(selectedRole);
      setTimeout(() => {
        dispatch(setGlobalLoader(false));
        navigate(redirectPath);
      }, 500);
    } catch (error) {
      setSwitching(false);
      dispatch(setGlobalLoader(false));
      console.log('Error switching role:', error);
      toast.error(error.response?.data?.message ?? 'Error switching role');
    }
  };

  return (
    <>
    <Box
      display={'flex'}
      justifyContent={'space-between'}
      alignItems={'center'}
      width={1}
    >
      <Box display={'flex'} alignItems={'center'} color={'primary.dark'}>
        <Box component={'button'} onClick={() => navigate(getRedirectPath(currentProfile))} style={{ all: "unset" }}>
          <img src={'/images/drivolo-icon.png'} alt={'Drivolo'} height="40vh" />
        </Box>
        
        <Typography
          marginLeft={1}
          color="text.primary"
          sx={{ fontFamily: "'Bank Gothic', sans-serif", fontWeight: 700, letterSpacing: 1, display: { xs: 'none', sm: 'block' } }}
        >
          Drivolo
        </Typography>
      </Box>
      <Box display={'flex'} alignItems={'center'}>
        {/* Profile Chip */}
        <Box marginRight={{ xs: 1, md: 2 }}>
          <Chip
            icon={<SwapHorizIcon />}
            label={currentProfile || 'Profile'}
            onClick={handleProfileClick}
            color="primary"
            variant="outlined"
            sx={{
              cursor: 'pointer',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          />
        </Box>
        <Box marginLeft={{ xs: 1, md: 2 }}>
          <Button
            variant="contained"
            color="primary"
            component="button"
            size="large"
            onClick={() => {
              let text = "Logout Confirmation.";
              if (window.confirm(text) === true) {
                dispatch(logout())
              }
            }}
          >
            logout
          </Button>
        </Box>
        <Box sx={{ display: { xs: 'block', md: 'none' } }} marginLeft={2}>
          <Button
            onClick={() => onSidebarOpen()}
            aria-label="Menu"
            variant={'outlined'}
            sx={{
              borderRadius: 2,
              minWidth: 'auto',
              padding: 1,
              borderColor: alpha(theme.palette.divider, 0.2),
            }}
          >
            <MenuIcon />
          </Button>
        </Box>
      </Box>

      {/* Role Switch Dialog */}
      <Dialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Switch Role</DialogTitle>
        <DialogContent>
          {loadingRoles ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : availableRoles.length === 0 ? (
            <Typography color="text.secondary">No roles available</Typography>
          ) : availableRoles.length === 1 ? (
            <Typography color="text.secondary">
              You only have one role: <strong>{availableRoles[0]}</strong>
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a role to switch to:
              </Typography>
              <FormControl>
                <RadioGroup
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  {availableRoles.map((role) => (
                    <FormControlLabel
                      key={role}
                      value={role}
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {role}
                          {role === currentProfile && (
                            <Typography variant="caption" color="primary">
                              (Current)
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)} disabled={switching}>
            Cancel
          </Button>
          {availableRoles.length > 1 && (
            <Button
              variant="contained"
              onClick={handleRoleSwitch}
              disabled={switching || selectedRole === currentProfile}
            >
              {switching ? <CircularProgress size={20} /> : 'Switch'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
    <Box
      sx={{
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        width: '100vw',
        mt: 1,
      }}
    >
      <HelplineBar />
    </Box>
    </>
  );
};

Topbar.propTypes = {
  onSidebarOpen: PropTypes.func,
};

export default Topbar;
